<template lang="pug">
    .interactive-builder
        //- Header: none / a short bold title / a wide image. The image picker reuses the shared
        //- media library dialog (real upload dropzone + library grid); once chosen a thumbnail
        //- with replace/remove sits beside it.
        el-form-item(v-if="!collapsed" :label="$t('Header')")
            el-radio-group.interactive-builder__header-type(:value="value.header_format" size="mini" @input="setHeaderFormat")
                el-radio-button(label="NONE") {{ $t('None') }}
                el-radio-button(label="TEXT") {{ $t('Text') }}
                el-radio-button(label="IMAGE") {{ $t('Image') }}
            template(v-if="value.header_format === 'IMAGE'")
                //- The project's canonical single-image pattern (mirrors BusinessCardForm): a
                //- 150px preview tile with an edit overlay + a circle delete when set, else a
                //- dashed drop-tile. Both open the shared media library (real dropzone + grid).
                .interactive-builder__image(v-if="headerImageUrl")
                    a.interactive-builder__image-link(href="#" @click.prevent="openHeaderEditor")
                        img.interactive-builder__image-preview(:src="headerImageUrl" :alt="$t('Header image')")
                        .interactive-builder__image-edit
                            i.el-icon-fa-pencil-alt.fas
                    el-button.interactive-builder__image-delete(circle type="info" size="mini" icon="el-icon-fa-trash-alt far" @click="clearHeaderImage")
                el-upload.plus-box(
                    v-else
                    ref="headerUpload"
                    drag
                    :action="uploadEndpoint"
                    accept="image/jpg, image/jpeg, image/png"
                    :before-upload="validateUpload"
                    :on-change="updateUploadHeaders"
                    :on-success="onHeaderUploadSuccess"
                    :on-error="onUploadError"
                    :headers="uploadHeaders"
                    :data="uploadData"
                )
                    i.el-icon-plus
                    .el-upload__text(v-html="$t('upload.drop.singular')")
                    .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '12mb' }) }}
                el-button.interactive-builder__library-btn(
                    v-if="!headerImageUrl"
                    plain
                    size="small"
                    icon="el-icon-picture-outline"
                    @click="headerMediaVisible = true"
                ) {{ $t('Select from library') }}
                image-library-dialog(:visible.sync="headerMediaVisible" selection-type="image" :accepted-mime-types="headerImageMimeTypes" @select="onHeaderImageSelected")
                //- The project's unified image editor (same as BusinessCard), locked to 4:1 so the
                //- crop matches WhatsApp; it bakes the crop server-side and emits the fresh image.
                //- Kept always-mounted (NOT v-if="headerImage"): the editor only runs its
                //- onOpen() — which seeds the 4:1 aspect lock — on a hidden→visible transition.
                //- Mounting it together with visible=true skips that transition, so the lock
                //- never seeds. Mirrors the canonical ImageContent invocation.
                generate-image-edit-drawer(
                    :visible.sync="imageEditorVisible"
                    :image="headerImage"
                    :fixed-aspect-ratio="headerAspectRatio"
                    start-in-crop
                    :mutable="true"
                    @updated="onHeaderImageUpdated"
                    @replaced="onHeaderImageReplaced"
                )
                span.help {{ $t('Shown at the top of the message; WhatsApp crops it to a wide 4:1 banner, so the picker lets you choose exactly what shows. JPG or PNG.') }}
            template(v-else-if="value.header_format === 'TEXT'")
                el-input(:value="value.header" maxlength="60" show-word-limit @input="setField('header', $event)")
                span.help {{ $t('A short bold title above the message.') }}
        //- The message body goes here (via a host slot) so the form matches the real card order:
        //- header, then message, then footer + buttons. Empty in the composer (its body lives
        //- above the disclosure toggle).
        slot
        el-form-item(v-if="!collapsed" :label="$t('Footer')")
            el-input(:value="value.footer" maxlength="60" show-word-limit @input="setField('footer', $event)")
            span.help {{ $t('A small note under the message.') }}
        el-form-item(v-if="!collapsed" :label="$t('Buttons')")
            //- Free-form (in-window): WhatsApp allows EITHER up to 3 quick replies OR a single link
            //- button — never mixed, no phone button. A whole-message mode keeps it valid by design.
            template(v-if="freeForm")
                el-radio-group.interactive-builder__button-mode(:value="buttonMode" size="mini" @input="setButtonMode")
                    el-radio-button(label="none") {{ $t('None') }}
                    el-radio-button(label="quick") {{ $t('Quick replies') }}
                    el-radio-button(label="link") {{ $t('Link') }}
                .interactive-builder__buttons(v-if="buttonMode !== 'none' && value.buttons.length")
                    .interactive-builder__button(v-for="(button, index) in value.buttons" :key="index")
                        .interactive-builder__button-head
                            el-input(:value="button.text" size="small" :placeholder="buttonMode === 'link' ? $t('Button text') : $t('Quick reply text')" maxlength="25" @input="setButtonField(index, 'text', $event)")
                            el-button.interactive-builder__button-remove(v-if="buttonMode === 'quick'" type="text" icon="el-icon-fa-trash-can far" @click="removeButton(index)")
                        .interactive-builder__button-body(v-if="buttonMode === 'link'")
                            link-picker(:value="button.url" :allow-order="allowOrder" :member-id="memberId" @input="setButtonField(index, 'url', $event)")
                el-button.interactive-builder__button-add(v-if="buttonMode === 'quick' && value.buttons.length < 3" plain size="small" icon="el-icon-fa-plus fas" @click="addButton('QUICK_REPLY')") {{ $t('Add quick reply') }}
                span.help(v-if="buttonMode === 'quick'") {{ $t('Up to 3 tap-to-reply buttons.') }}
                span.help(v-else-if="buttonMode === 'link'") {{ $t('One link button. Pick its link below.') }}
                span.help(v-else) {{ $t('WhatsApp allows either up to 3 quick replies or a single link button.') }}
            //- Templates: the flexible per-button types (Meta allows mixing, within its template
            //- limits — up to 2 links, 1 phone, quick replies grouped).
            template(v-else)
                .interactive-builder__buttons(v-if="value.buttons.length")
                    .interactive-builder__button(v-for="(button, index) in value.buttons" :key="index")
                        .interactive-builder__button-head
                            el-radio-group(:value="button.type" size="mini" @input="setButtonType(index, $event)")
                                el-radio-button(label="URL") {{ $t('Link') }}
                                el-radio-button(label="QUICK_REPLY") {{ $t('Quick reply') }}
                                el-radio-button(label="PHONE_NUMBER") {{ $t('Call') }}
                            el-button.interactive-builder__button-remove(type="text" icon="el-icon-fa-trash-can far" @click="removeButton(index)")
                        el-input(:value="button.text" size="small" :placeholder="$t('Button text')" maxlength="25" @input="setButtonField(index, 'text', $event)")
                        .interactive-builder__button-body(v-if="button.type === 'URL'")
                            link-picker(:value="button.url" :allow-order="allowOrder" :member-id="memberId" @input="setButtonField(index, 'url', $event)")
                        .interactive-builder__button-body(v-else-if="button.type === 'PHONE_NUMBER'")
                            el-input(:value="button.phone_number" size="small" placeholder="+49…" @input="setButtonField(index, 'phone_number', $event)")
                el-button.interactive-builder__button-add(v-if="canAddTemplateButton" plain size="small" icon="el-icon-fa-plus fas" @click="addTemplateButton") {{ $t('Add button') }}
                span.help {{ $t('Up to 10 buttons: at most 2 links, 1 phone number, the rest quick replies.') }}
</template>

<i18n>
{
    "de": {
        "Select from library": "Aus Mediathek wählen",
        "Header": "Überschrift",
        "None": "Keine",
        "Text": "Text",
        "Image": "Bild",
        "Header image": "Überschriftsbild",
        "Shown at the top of the message; a wide image looks best. JPG or PNG.": "Wird oben in der Nachricht angezeigt; ein breites Bild wirkt am besten. JPG oder PNG.",
        "Shown at the top of the message; WhatsApp crops it to a wide 4:1 banner, so the picker lets you choose exactly what shows. JPG or PNG.": "Wird oben in der Nachricht angezeigt; WhatsApp schneidet es auf ein breites 4:1-Banner zu. Im Zuschneider legst du fest, was genau zu sehen ist. JPG oder PNG.",
        "A short bold title above the message.": "Ein kurzer fetter Titel über der Nachricht.",
        "Footer": "Fußzeile",
        "A small note under the message.": "Ein kleiner Hinweis unter der Nachricht.",
        "Buttons": "Schaltflächen",
        "Link": "Link",
        "Quick reply": "Schnellantwort",
        "Quick replies": "Schnellantworten",
        "Call": "Anruf",
        "Button text": "Beschriftung",
        "Quick reply text": "Text der Schnellantwort",
        "Add button": "Schaltfläche hinzufügen",
        "Add quick reply": "Schnellantwort hinzufügen",
        "Up to 3 tap-to-reply buttons.": "Bis zu 3 Tipp-zum-Antworten-Schaltflächen.",
        "One link button. Pick its link below.": "Eine Link-Schaltfläche. Wähle unten ihr Ziel.",
        "WhatsApp allows either up to 3 quick replies or a single link button.": "WhatsApp erlaubt entweder bis zu 3 Schnellantworten oder eine einzelne Link-Schaltfläche.",
        "Up to 10 buttons: at most 2 links, 1 phone number, the rest quick replies.": "Bis zu 10 Schaltflächen: höchstens 2 Links, 1 Telefonnummer, der Rest Schnellantworten.",
        "A template allows at most 2 link buttons.": "Eine Vorlage erlaubt höchstens 2 Link-Schaltflächen.",
        "A template allows only 1 phone-number button.": "Eine Vorlage erlaubt nur 1 Telefonnummer-Schaltfläche."
    }
}
</i18n>

<script>
import _ from 'lodash'
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'
import GenerateImageEditDrawer from '@/extensions/generative-media/views/Drawers/GenerateImageEditDrawer'
import LinkPicker from '@/extensions/communication/views/Conversation/Partials/LinkPicker'
import { ensureFreshToken } from '@/helpers'
import { headerPreviewUrl } from '@/extensions/communication/utils/headerImageInput'
import { adminClient } from '@/store/apollo'
import ImportHeaderImageGql from '@/extensions/communication/store/graphql/mutations/ImportHeaderImage.gql'
import { useAuthStore, useSetupStore } from '@/stores'

// WhatsApp displays the interactive header image at a fixed 4:1 ratio (official spec), center-
// cropped. We let the admin control that crop with the project's unified image editor locked to
// 4:1 — the same one BusinessCard uses (it bakes the crop server-side into a variant File, which
// the send then delivers). No bespoke cropper/mutation.
const HEADER_ASPECT_RATIO = 4

// One authoring surface for a WhatsApp interactive message's chrome — header (none/text/image),
// footer, and up to three buttons — shared by the template create form, the free-form composer,
// and the auto-reply editor so the three never drift. The body/message stays with each host (it
// carries context-specific concerns: template placeholders, the live composer editor, etc.).
//
// v-model is the interactive object:
//   { header_format: 'NONE'|'TEXT'|'IMAGE', header, header_image (an ImageInput carrying the
//     admin's 4:1 crop in settings.processing — persisted + baked server-side, exactly like a
//     BusinessCard image), header_media_url (display fallback),
//     footer, buttons: [{ type: 'URL'|'QUICK_REPLY'|'PHONE_NUMBER', text, url, phone_number }] }
// Updates are emitted immutably (a fresh object) — the component never mutates the prop.
export default {
    name: 'WhatsAppInteractiveBuilder',
    components: { ImageLibraryDialog, GenerateImageEditDrawer, LinkPicker },
    props: {
        value: {
            type: Object,
            required: true
        },
        // Offer the "Order details" / "Track shipment" links in a button's LinkPicker (only
        // where the thread has a member whose orders can be listed). Off for templates.
        allowOrder: {
            type: Boolean,
            default: false
        },
        // The thread's member — passed to the LinkPicker so an order/tracking button lists
        // THIS member's orders (a chat isn't tied to one order; the agent picks per message).
        memberId: {
            type: Number,
            default: null
        },
        // Free-form (in-window) context — the composer + auto-reply. WhatsApp free-form
        // interactive messages support quick replies OR one link, never a phone button (that
        // needs an approved template), so hide the phone option and adjust the hint.
        freeForm: {
            type: Boolean,
            default: false
        },
        // Composer disclosure: when true, only the message (the slot) shows — the header,
        // footer and buttons are hidden until the agent expands them. Keeps a plain reply
        // plain while still ordering header → message → footer → buttons once expanded.
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            headerMediaVisible: false,
            imageEditorVisible: false,
            // True while a URL-only header image is being materialized into an editable Image
            // (the pencil click on a Meta/legacy header) so the editor can open on it.
            importingHeaderImage: false,
        }
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json'
            }
        },
        // Upload a new header image to the CMS's PUBLIC bucket (so Meta can fetch it directly),
        // exactly like BusinessCardForm.
        uploadEndpoint() {
            const base = useSetupStore().uploadEndpoint
            return this.cmsId ? `${base}/public/${this.cmsId}/` : base
        },
        uploadData() {
            const data = { 'context[type]': 'communication-header-image' }
            if (this.cmsId) {
                data['context[model]'] = 'CityPages\\Cms'
                data['context[id]'] = this.cmsId
            }
            return data
        },
        cmsId() {
            return (useSetupStore().activeCms || {}).id || null
        },
        // The header Image object (an ImageInput) the editor edits and the save persists.
        headerImage() {
            return this.value.header_image || null
        },
        // Browser-displayable URL for the header preview tile — FileController serves the EXACT
        // crop (private-safe), same as BusinessCard/ImageContent. Falls back to a loaded header's
        // stored url (auto-reply reopen, where header_image isn't rehydrated).
        headerImageUrl() {
            return headerPreviewUrl(this.value.header_image) || this.value.header_media_url || ''
        },
        headerAspectRatio() {
            return HEADER_ASPECT_RATIO
        },
        // WhatsApp header images must be JPG or PNG.
        headerImageMimeTypes() {
            return ['image/jpeg', 'image/png']
        },
        // Free-form button mode, derived from the authored buttons (link wins if a URL exists).
        buttonMode() {
            const buttons = this.value.buttons || []
            if (buttons.some(button => button.type === 'URL')) return 'link'
            if (buttons.some(button => button.type === 'QUICK_REPLY')) return 'quick'
            return 'none'
        },
        // Templates allow up to 10 buttons total (Meta).
        canAddTemplateButton() {
            return (this.value.buttons || []).length < 10
        }
    },
    methods: {
        emit(next) {
            this.$emit('input', next)
        },
        setField(field, fieldValue) {
            this.emit({ ...this.value, [field]: fieldValue })
        },
        setHeaderFormat(format) {
            const next = { ...this.value, header_format: format }
            if (format !== 'IMAGE') {
                next.header_image = null
                next.header_media_url = ''
            }
            if (format !== 'TEXT') next.header = ''
            this.emit(next)
        },
        // A header IMAGE is picked from the library → set it as the header NOW (so it sticks even
        // without a manual crop, like BusinessCard) then open the unified editor locked to 4:1 so
        // the admin can refine exactly what WhatsApp shows. The save persists it + bakes the crop.
        onHeaderImageSelected(selection) {
            const image = Array.isArray(selection) ? selection[0] : selection
            if (!image) return
            const headerImage = { ...image, type: 'communication-header-image' }
            this.emit({ ...this.value, header_image: headerImage, header_media_url: headerPreviewUrl(headerImage) })
            // Defer opening so the drawer mounts (visible=false) before the hidden→visible
            // transition that runs its onOpen() (which seeds the 4:1 aspect lock).
            this.$nextTick(() => { this.imageEditorVisible = true })
        },
        // Clicking the preview re-opens the editor (or the library if there's nothing to edit).
        async openHeaderEditor() {
            // A freshly-picked or already-rehydrated image opens the editor directly.
            if (this.headerImage) { this.imageEditorVisible = true; return }
            // Only a URL (a template loaded from Meta, or a legacy header saved before its crop
            // was persisted): materialize it into an editable Image so the SAME editor can
            // re-crop it. Falls back to the library when the URL can't be fetched.
            if (this.headerImageUrl && !this.importingHeaderImage) {
                this.importingHeaderImage = true
                const image = await this.importHeaderImageFromUrl(this.headerImageUrl)
                this.importingHeaderImage = false
                if (image) {
                    this.emit({ ...this.value, header_image: image, header_media_url: headerPreviewUrl(image) || this.value.header_media_url })
                    this.$nextTick(() => { this.imageEditorVisible = true })
                    return
                }
            }
            this.headerMediaVisible = true
        },
        importHeaderImageFromUrl(url) {
            if (!this.cmsId || !url) return Promise.resolve(null)
            // cms_id is an Int! at the schema; activeCms.id is a string, so coerce it.
            return adminClient
                .mutate({ mutation: ImportHeaderImageGql, variables: { cms_id: parseInt(this.cmsId, 10), url } })
                .then(({ data }) => (data && data.EX_COMM_ImportHeaderImage) || null)
                .catch(() => null)
        },
        // The editor emits the fresh image after a crop — `updated` (deferred crop synced onto the
        // working image) or `replaced` (source swapped). Sync the crop-bearing keys into
        // value.header_image so the save sends the current state (mirrors BusinessCard).
        onHeaderImageUpdated(image) {
            this.adoptEditedHeaderImage(image)
        },
        onHeaderImageReplaced(image) {
            this.adoptEditedHeaderImage(image)
        },
        adoptEditedHeaderImage(image) {
            if (!image) return
            const keys = ['processing_query_string', 'processedFile', 'activeVariant', 'variants', 'settings', 'source', 'width', 'height']
            const next = { ...(this.value.header_image || {}), ...image, type: 'communication-header-image' }
            keys.forEach(key => { next[key] = _.get(image, key) })
            this.emit({ ...this.value, header_image: next, header_media_url: headerPreviewUrl(next) })
        },
        clearHeaderImage() {
            this.emit({ ...this.value, header_image: null, header_media_url: '' })
        },
        // --- Upload plumbing, copied from BusinessCardForm (public bucket → Meta-fetchable) ---
        async validateUpload(file) {
            await this.updateUploadHeaders()
            if (file.size / 1024 / 1024 > 12) {
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '12mb' }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                })
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject()
            }
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        // A fresh upload → open the editor on it so the admin crops to 4:1 (its baked variant
        // becomes the header, same path as a library pick).
        onHeaderUploadSuccess(response) {
            const image = response
            image.upload = true
            image.type = 'communication-header-image'
            image.width = _.get(response, 'dimensions.width')
            image.height = _.get(response, 'dimensions.height')
            if (typeof image.size === 'number') image.size = image.size.toString()
            delete image.hash
            delete image.dimensions
            this.emit({ ...this.value, header_image: image, header_media_url: headerPreviewUrl(image) })
            this.$nextTick(() => { this.imageEditorVisible = true })
        },
        onUploadError() {
            this.$notify.error({
                title: this.$t('Upload failed'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 4000
            })
        },
        addButton(type = 'URL') {
            this.emit({ ...this.value, buttons: [...this.value.buttons, { type, text: '', url: '', phone_number: '' }] })
        },
        // Template add: pick a type that keeps the message valid — a link until the 2-link cap,
        // then quick replies (Meta: max 2 links, 1 phone, up to 10 total). Phone is a deliberate
        // switch by the user, never the auto-added default.
        addTemplateButton() {
            const buttons = this.value.buttons || []
            const linkCount = buttons.filter(button => button.type === 'URL').length
            this.addButton(linkCount < 2 ? 'URL' : 'QUICK_REPLY')
        },
        // Free-form button mode (Design A): WhatsApp allows EITHER up to 3 quick replies OR one
        // link button, never mixed. Switching mode resets to a single button of that kind.
        setButtonMode(mode) {
            let buttons = []
            if (mode === 'quick') buttons = [{ type: 'QUICK_REPLY', text: '', url: '', phone_number: '' }]
            else if (mode === 'link') buttons = [{ type: 'URL', text: '', url: '', phone_number: '' }]
            this.emit({ ...this.value, buttons })
        },
        removeButton(index) {
            this.emit({ ...this.value, buttons: this.value.buttons.filter((button, current) => current !== index) })
        },
        setButtonType(index, type) {
            // Meta template limits: at most 2 link buttons and 1 phone button. Block a switch that
            // would exceed them (leave the button on its current type).
            const others = this.value.buttons.filter((button, current) => current !== index)
            if (type === 'URL' && others.filter(button => button.type === 'URL').length >= 2) {
                this.notifyButtonLimit(this.$t('A template allows at most 2 link buttons.'))
                return
            }
            if (type === 'PHONE_NUMBER' && others.some(button => button.type === 'PHONE_NUMBER')) {
                this.notifyButtonLimit(this.$t('A template allows only 1 phone-number button.'))
                return
            }
            this.emit({ ...this.value, buttons: this.value.buttons.map((button, current) => (current === index ? { ...button, type } : button)) })
        },
        notifyButtonLimit(message) {
            this.$notify.error({ title: message, iconClass: 'fas el-icon-fa-times', position: 'bottom-left', customClass: 'dark', duration: 6000 })
        },
        setButtonField(index, field, fieldValue) {
            this.emit({ ...this.value, buttons: this.value.buttons.map((button, current) => (current === index ? { ...button, [field]: fieldValue } : button)) })
        }
    }
}
</script>

<style lang="sass" scoped>
.interactive-builder
    // Project hint convention: small, grey, tight line-height directly under the field.
    .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
        margin-top: 4px
    .interactive-builder__header-type
        margin-bottom: 8px
    // Header image — the project's canonical single-image tile (see BusinessCardForm): a 150px
    // preview with a hover edit overlay + a circle delete, or a dashed drop-tile when empty.
    .interactive-builder__image
        position: relative
        display: inline-block
        .interactive-builder__image-link
            display: block
            position: relative
            cursor: pointer
            border-radius: 4px
            overflow: hidden
            &:hover .interactive-builder__image-edit
                opacity: 1
        .interactive-builder__image-preview
            display: block
            // Preview EXACTLY how WhatsApp renders the header: capped at a 4:1 width/height ratio
            // (official spec), a wider image center-cropped to 4:1. Matches the bubble + phone.
            width: 320px
            max-width: 100%
            aspect-ratio: 4 / 1
            object-fit: cover
            border-radius: 4px
            border: 1px solid #dcdfe6
        .interactive-builder__image-edit
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            display: flex
            align-items: center
            justify-content: center
            background: rgba(0, 0, 0, 0.5)
            opacity: 0
            transition: opacity 0.15s ease
            border-radius: 4px
            i
                color: #fff
                font-size: 24px
        .interactive-builder__image-delete
            position: absolute
            top: -10px
            right: -10px
            z-index: 10
            width: 30px
            height: 30px
    .interactive-builder__library-btn
        margin-top: 8px
    // Free-form: the whole-message button mode (None / Quick replies / Link).
    .interactive-builder__button-mode
        margin-bottom: 8px
    // Button authoring: each button is its own quiet card.
    .interactive-builder__buttons
        display: flex
        flex-direction: column
        gap: 10px
        margin-bottom: 8px
        .interactive-builder__button
            display: flex
            flex-direction: column
            gap: 8px
            padding: 10px 12px
            background: #fff
            border: 1px solid #ebeef5
            border-radius: 8px
            .interactive-builder__button-head
                display: flex
                align-items: center
                justify-content: space-between
                gap: 8px
                // The quick-reply/link text input takes the row; the remove button sits at the end.
                .el-input
                    flex: 1
            .interactive-builder__button-remove
                flex-shrink: 0
                padding: 0
                color: #c0c4cc
                &:hover
                    color: #f56c6c
    .interactive-builder__button-add
        align-self: flex-start
</style>
